import React, { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, FlaskConical, Droplets, FlaskConical as Flask, Wind, Scan, Microscope } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusBadge, StatusToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { labTestCategoryService } from '../services/api';

/* ── Sample type options (shared) ───────────────────────────────────────────── */
export const SAMPLE_TYPE_OPTIONS = [
  { value: 'Blood',   label: 'Blood',   icon: Droplets,  color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20'       },
  { value: 'Urine',   label: 'Urine',   icon: Flask,     color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
  { value: 'Saliva',  label: 'Saliva',  icon: Wind,      color: 'text-medihub-primary',   bg: 'bg-medihub-light dark:bg-medihub-dark/20'     },
  { value: 'Imaging', label: 'Imaging', icon: Scan,      color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
  { value: 'Other',   label: 'Other',   icon: Microscope,color: 'text-gray-500',   bg: 'bg-gray-50 dark:bg-gray-700/50'     },
];

export const UNIT_OPTIONS = ['ml', 'mg', 'g', 'µl', 'strips', 'swabs', 'session', 'other'];

export const getSampleTypeMeta = (type) =>
  SAMPLE_TYPE_OPTIONS.find(s => s.value === type) || SAMPLE_TYPE_OPTIONS[4];

/* ── Sample Mapping Builder ─────────────────────────────────────────────────── */
const SampleMappingBuilder = ({ mappings, onChange }) => {
  const addRow = () => onChange([...mappings, { sampleType: '', quantity: '', unit: 'ml' }]);

  const removeRow = (i) => onChange(mappings.filter((_, idx) => idx !== i));

  const updateRow = (i, field, val) =>
    onChange(mappings.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Sample Mappings <span className="text-red-500 ml-0.5">*</span>
        </label>
        <button
          type="button"
          onClick={addRow}
          className="text-xs text-medihub-primary dark:text-medihub-highlight hover:underline flex items-center gap-1 font-medium"
        >
          <Plus className="w-3.5 h-3.5" />Add Sample
        </button>
      </div>

      {mappings.length === 0 && (
        <p className="text-xs text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-3 py-2 rounded-lg">
          At least one sample mapping is required.
        </p>
      )}

      <div className="space-y-2">
        {mappings.map((row, i) => {
          const meta = getSampleTypeMeta(row.sampleType);
          const Icon = meta.icon;
          return (
            <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-200 dark:border-gray-600">
              {/* Sample type icon preview */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${row.sampleType ? meta.bg : 'bg-gray-100 dark:bg-gray-700'}`}>
                <Icon className={`w-4 h-4 ${row.sampleType ? meta.color : 'text-gray-400'}`} />
              </div>

              {/* Sample Type */}
              <select
                value={row.sampleType}
                onChange={e => updateRow(i, 'sampleType', e.target.value)}
                className="input-field flex-1 py-1.5 text-sm"
              >
                <option value="">Sample type…</option>
                {SAMPLE_TYPE_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              {/* Quantity */}
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={row.quantity}
                onChange={e => updateRow(i, 'quantity', e.target.value)}
                placeholder="Qty"
                className="input-field w-20 py-1.5 text-sm text-center"
              />

              {/* Unit */}
              <select
                value={row.unit}
                onChange={e => updateRow(i, 'unit', e.target.value)}
                className="input-field w-24 py-1.5 text-sm"
              >
                {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>

              {/* Remove */}
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Preview */}
      {mappings.some(m => m.sampleType && m.quantity) && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {mappings.filter(m => m.sampleType && m.quantity).map((m, i) => {
            const meta = getSampleTypeMeta(m.sampleType);
            const Icon = meta.icon;
            return (
              <span key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.color}`}>
                <Icon className="w-3 h-3" />
                {m.sampleType} — {m.quantity} {m.unit}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
  },
];
const filterFn = (item, f) => {
  if (f.search && !item.name.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.status && item.status !== f.status) return false;
  return true;
};

/* ── Category Form ──────────────────────────────────────────────────────────── */
const CategoryForm = ({ initial, existingNames, onSubmit, onClose }) => {
  const rules = { name: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial
      ? { name: initial.name, description: initial.description || '', status: initial.status }
      : { name: '', description: '', status: 'active' },
    rules
  );

  const [mappings, setMappings] = useState(
    initial?.sampleMappings?.length
      ? initial.sampleMappings.map(m => ({ ...m, quantity: String(m.quantity) }))
      : [{ sampleType: '', quantity: '', unit: 'ml' }]
  );

  const handleFormSubmit = (vals) => {
    const trimmed = vals.name.trim();
    const isDuplicate = existingNames
      .filter(n => !initial || n.toLowerCase() !== initial.name.toLowerCase())
      .some(n => n.toLowerCase() === trimmed.toLowerCase());
    if (isDuplicate) { toast.error('Category already exists'); return; }

    // Validate mappings
    const validMappings = mappings.filter(m => m.sampleType && m.quantity);
    // if (validMappings.length === 0) { toast.error('Add at least one sample mapping'); return; }
    const hasInvalidQty = validMappings.some(m => parseFloat(m.quantity) <= 0);
    if (hasInvalidQty) { toast.error('Sample quantity must be positive'); return; }

    onSubmit({
      ...vals,
      name: trimmed,
      sampleMappings: validMappings.map(m => ({
        sampleType: m.sampleType,
        quantity: parseFloat(m.quantity),
        unit: m.unit,
      })),
    });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <FormField label="Category Name" error={errors.name} required>
        <input
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          className="input-field"
          placeholder="e.g. Blood Test, Imaging, Hormone Test…"
          autoFocus
        />
      </FormField>

      {/* <FormField label="Description">
        <textarea
          name="description"
          value={values.description}
          onChange={handleChange}
          className="input-field resize-none"
          rows={2}
          placeholder="Brief description of this category…"
        />
      </FormField> */}

      {/* Sample Mapping Builder */}
      {/* <SampleMappingBuilder mappings={mappings} onChange={setMappings} /> */}

      <FormField label="Status">
        <div className="flex gap-4">
          {['active', 'inactive'].map(s => (
            <label key={s} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="status" value={s} checked={values.status === s} onChange={handleChange} className="accent-blue-600" />
              <StatusBadge status={s} dot={false} />
            </label>
          ))}
        </div>
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update Category' : 'Add Category'}</button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const LabTestCategories = () => {
  const { data: categories, loading, refetch } = useFetch(() => labTestCategoryService.getAll());

  // Map API response fields to component expected fields
  const mappedCategories = (categories || []).map(c => ({
    ...c,
    status: c.is_active === 1 ? 'active' : 'inactive',
    name: c.name,
  }));
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, mappedCategories);

  const existingNames = mappedCategories?.map(c => c.name) || [];

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await labTestCategoryService.update(selected.id, vals);
        toast.success('Category updated!');
      } else {
        const res = await labTestCategoryService.create({
          name:       vals.name,
          sort_order: 1,
          is_active:  vals.status === 'active' ? 1 : 0,
        });
        toast.success(res?.message || 'Category added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Something went wrong'); }
  };

  const handleDelete = async (id) => {
    const cat = categories?.find(c => c.id === id);
    if (cat?.testCount > 0) { toast.error(`Cannot delete — ${cat.testCount} test(s) assigned`); return; }
    if (!window.confirm('Delete this category?')) return;
    try { await labTestCategoryService.delete(id); toast.success('Deleted'); refetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (cat) => {
    try {
      const res = await labTestCategoryService.toggleStatus(cat.id, cat.status || cat.is_active);
      toast.success(res?.message || 'Status updated');
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to update'); }
  };

  const columns = [
    {
      key: 'name', label: 'Category', render: (v, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-teal-100 dark:bg-teal-900/40 rounded-xl flex items-center justify-center shrink-0">
            <FlaskConical className="w-4 h-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{v}</p>
            {row.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-[180px] truncate">{row.description}</p>}
          </div>
        </div>
      )
    },
    // {
    //   key: 'sampleMappings', label: 'Sample Mappings', render: (mappings) => {
    //     if (!mappings?.length) return <span className="text-xs text-gray-400">—</span>;
    //     return (
    //       <div className="flex flex-col gap-1">
    //         {mappings.map((m, i) => {
    //           const meta = getSampleTypeMeta(m.sampleType);
    //           const Icon = meta.icon;
    //           return (
    //             <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.bg} ${meta.color} w-fit`}>
    //               <Icon className="w-3 h-3" />
    //               {m.sampleType} — {m.quantity} {m.unit}
    //             </span>
    //           );
    //         })}
    //       </div>
    //     );
    //   }
    // },
    {
      key: 'testCount', label: 'Tests', render: v => (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-50 dark:bg-teal-900/30 text-sm font-semibold text-teal-700 dark:text-teal-300">{v}</span>
      )
    },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggle(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  const activeCount2 = categories?.filter(c => c.status === 'active').length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lab Test Categories</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage diagnostic test categories with sample mappings</p>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Category
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total',    value: categories?.length || 0,                          color: 'text-gray-900 dark:text-white'        },
          { label: 'Active',   value: activeCount2,                                      color: 'text-green-700 dark:text-green-400'   },
          { label: 'Inactive', value: (categories?.length || 0) - activeCount2,          color: 'text-red-600 dark:text-red-400'       },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <FilterBar filters={filters} onChange={onFilterChange} onReset={onReset} searchPlaceholder="Search category…" fields={FILTER_FIELDS} activeCount={activeCount} />

      <Table
        title={`Categories (${filtered.length}${activeCount ? ` of ${categories?.length || 0}` : ''})`}
        columns={columns} data={filtered} loading={loading} exportFilename="lab-test-categories"
      />

      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => { setModal(null); setSelected(null); }}
        title={modal === 'edit' ? 'Edit Category' : 'Add Category'}
        size="md"
      >
        <CategoryForm
          initial={modal === 'edit' ? selected : null}
          existingNames={existingNames}
          onSubmit={handleSave}
          onClose={() => { setModal(null); setSelected(null); }}
        />
      </Modal>
    </div>
  );
};

export default LabTestCategories;
